/**
 * Whole-grid transforms that stay chunk-aware (docs/large-volume-plan.md §3.1 `geom.translate`).
 */
import { posKeyXYZ } from '@/voxelMath'
import { ChunkedVoxelGrid } from './ChunkedVoxelGrid'
import { copyChunk, recountChunk, type VoxelChunk } from './chunk'
import { CHUNK_MASK, CHUNK_SHIFT, CHUNK_SIZE, CHUNKS_PER_AXIS, chunkClipTable, chunkCoords, chunkIdOfChunkXYZ, chunkOrigin } from './layout'

/**
 * Returns a new frozen grid (same palette and bounds) with every voxel moved by (dx, dy, dz).
 * Voxels that leave the bounds are dropped. Translations by whole chunks share chunk refs with the
 * source (O(chunks)); other offsets copy cell by cell (O(voxels)) without re-interning.
 */
export function translateGrid(g: ChunkedVoxelGrid, dx: number, dy: number, dz: number): ChunkedVoxelGrid {
  dx |= 0; dy |= 0; dz |= 0
  if (dx === 0 && dy === 0 && dz === 0) return g
  const out = new ChunkedVoxelGrid(g.palette, g.bounds).asMutable()
  const b = out.bounds
  const aligned = (dx & CHUNK_MASK) === 0 && (dy & CHUNK_MASK) === 0 && (dz & CHUNK_MASK) === 0
  if (aligned) {
    const cdx = dx >> CHUNK_SHIFT, cdy = dy >> CHUNK_SHIFT, cdz = dz >> CHUNK_SHIFT
    const clip = chunkClipTable(b)
    g.forEachChunk((chunkId, chunk) => {
      if (chunk.count === 0) return
      const [cx, cy, cz] = chunkCoords(chunkId)
      const nx = cx + cdx, ny = cy + cdy, nz = cz + cdz
      if (nx < 0 || ny < 0 || nz < 0 || nx >= CHUNKS_PER_AXIS || ny >= CHUNKS_PER_AXIS || nz >= CHUNKS_PER_AXIS) return
      const nid = chunkIdOfChunkXYZ(nx, ny, nz)
      const c = clip === null ? 2 : clip[nid]
      if (c === 0) return
      if (c === 2) { out.setChunk(nid, chunk); return }
      // Partially inside: copy and clear the cells that fall outside the bounds.
      const copy: VoxelChunk = copyChunk(chunk, 0)
      const ox = nx << CHUNK_SHIFT, oy = ny << CHUNK_SHIFT, oz = nz << CHUNK_SHIFT
      const d = copy.data
      for (let i = 0; i < 4096; i++) {
        if (d[i] === 0) continue
        const x = ox + (i & 15), y = oy + ((i >> 4) & 15), z = oz + (i >> 8)
        if (x >= b.x || y >= b.y || z >= b.z) d[i] = 0
      }
      if (recountChunk(copy) > 0) out.setChunk(nid, copy)
    })
    return out.asImmutable()
  }
  g.forEachChunk((chunkId, chunk) => {
    if (chunk.count === 0) return
    const o = chunkOrigin(chunkId)
    const ox = o.x + dx, oy = o.y + dy, oz = o.z + dz
    // Skip chunks whose whole translated box misses the bounds.
    if (ox + CHUNK_SIZE <= 0 || oy + CHUNK_SIZE <= 0 || oz + CHUNK_SIZE <= 0 || ox >= b.x || oy >= b.y || oz >= b.z) return
    const d = chunk.data
    for (let i = 0; i < 4096; i++) {
      const id = d[i]
      if (id === 0) continue
      const x = ox + (i & 15), y = oy + ((i >> 4) & 15), z = oz + (i >> 8)
      if (x < 0 || y < 0 || z < 0 || x >= b.x || y >= b.y || z >= b.z) continue
      out.setId(posKeyXYZ(x, y, z), id)
    }
  })
  return out.asImmutable()
}
